/**
 * How often a screen should re-ask for scores.
 *
 * One rule, because two of them is what caused the problem: the picks cards
 * polled every 30 seconds and the ticker every 60, so during a game the same
 * score could differ between the card and the banner on one screen — and the
 * banner was always the stale one. Two numbers that were never meant to
 * disagree, quietly disagreeing.
 *
 * Fast only when it's worth it. A page open on a Tuesday doesn't need to ask
 * four times a minute, and asking anyway is how you end up rate-limited on the
 * day it matters.
 */

export const LIVE_MS = 15000;
export const IDLE_MS = 60000;

/**
 * `games` is whatever that surface last loaded.
 *
 * "Worth it" is wider than in_progress on purpose. A game whose kickoff has
 * passed but which still reads `scheduled` is precisely the one that needs
 * asking about — that's the gap where a started game looks like it hasn't,
 * and the surface can't resolve it without another request.
 */
export function pollInterval(games, now = Date.now()) {
  if (!games || !games.length) return IDLE_MS;
  const busy = games.some(g => {
    if (g.status === 'in_progress') return true;
    if (g.status !== 'scheduled' || !g.game_date_iso) return false;
    const kickoff = new Date(g.game_date_iso).getTime();
    // From five minutes before kickoff — when picks lock and people start
    // watching — until four hours after, by which point a game that still says
    // "scheduled" is a data problem, not a live one.
    return now >= kickoff - 5 * 60 * 1000 && now <= kickoff + 4 * 60 * 60 * 1000;
  });
  return busy ? LIVE_MS : IDLE_MS;
}
